<script lang="ts" setup>
import { useModule } from "@/stores/module";
import { useRouteParams } from "@vueuse/router";
import {
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  Sidebar,
  SidebarItem,
} from "vant";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useDrag } from "@/use/drag";
import { useGlobalModal } from "@/stores/globalModal";
import { SETTING_MODULE_ID } from "@/stores/module/moduleBase";
import { Mate, ModuleEntityType } from "@/types/entity";
import { useMate } from "@/stores/mate";
// 常量
// 状态
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
// const mateStore = useMate();
const moduleStore = useModule();
const uuid = useRouteParams("uuid", "");
const entityType = ref<ModuleEntityType>();
// const mate = ref<Mate>();

const entity = computed(() => {
  return moduleStore.module?.entity;
});

const module = computed(() => {
  return moduleStore.module;
});

const normalModuleList = computed(() => {
  return (
    module.value?.entity.moduleOrder?.[0]?.children
      ?.map((id) => module.value?.moduleIdMateModuleMap[id])
      .filter((v) => v) || []
  );
});

const getModuleId = () => {
  if (route.hash) return Number(route.hash.replace("#", ""));
  if (entity.value?.moduleOrder?.length)
    return entity.value?.moduleOrder?.[0]?.children?.[0];
  return 1;
};

const currentModuleId = ref<number>();

const currentTabIdx = ref(0);

watch(
  () => route.hash,
  () => {
    if (["mateEdit", "worldviewEdit"].includes(route.name as string)) {
      currentModuleId.value = getModuleId();
      // reloadModuleList();
      initCurrentTabIdx();
    }
  }
);

const initCurrentTabIdx = () => {
  let idx = normalModuleList.value.findIndex(
    (module) => currentModuleId.value === module?.moduleId
  );
  if (idx !== -1) return (currentTabIdx.value = idx);

  // 处理 隐私设置 模块
  currentTabIdx.value = normalModuleList.value.length;
};

const reloadModuleList = () => {
  showSideBar.value = false;
  nextTick(() => {
    showSideBar.value = true;
    initCurrentTabIdx();
  });
};

const loading = ref(false);
const isSaving = ref(false);
// 事件回调
const clickModule = (moduleId: number) => {
  if (isEditMode.value) {
    if (!moduleStore.module!.canEditModule(moduleId)) {
      return showFailToast(t("Message.cannotEditPresetModule"));
    }
    useGlobalModal().openTextInputModal({
      title: t("Message.createTemplateTitle"),
      callback: async (name?: string) => {
        if (!name) return;
        isSaving.value = true;
        await moduleStore.updateModule(moduleId, name);
        isSaving.value = false;
      },
    });
    return;
  }

  router.replace({ hash: `#${moduleId}` });
};

const showSideBar = ref(true);
const showCreateModule = ref(false);
// 拖拽

const scrollContainer = ref<HTMLElement>();
const isEditMode = ref(false);
const showDraggingTips = ref(false);
const {
  dragIndex,
  dragTargetIndex,
  dragStart,
  dragEnd,
  dragEnter,
  dragOver,
  touchStart,
  touchMove,
  touchEnd,
} = useDrag(
  { isDraggingMode: isEditMode, scrollContainer },
  (originIndex, targetIndex) => {
    if (originIndex === targetIndex) return;
    isSaving.value = true;
    const module = normalModuleList.value[originIndex];
    normalModuleList.value.splice(originIndex, 1);
    normalModuleList.value.splice(targetIndex, 0, module);
    const list = normalModuleList.value.map((v) => v!.moduleId);
    moduleStore.module?.updateMateModuleOrder(0, list).then(() => {
      isSaving.value = false;
    });
  }
);

const clickDeleteModule = async (moduleId: number) => {
  try {
    await showConfirmDialog({
      title: t("ConfirmModal.deleteModuleTitle"),
      message: t("ConfirmModal.deleteModuleInfo"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch {
    // 取消删除
    return;
  }
  const toast = showLoadingToast({
    message: t("Message.deletingModule"),
    duration: 0,
  });
  try {
    await moduleStore.deleteModule(moduleId);
    let newModuleId = getModuleId();
    if (moduleId === newModuleId) {
      newModuleId = normalModuleList.value[0]!.moduleId;
    }
    router.replace({
      hash: `#${newModuleId}`,
    });
  } catch (e) {
    showFailToast(t("Message.failDeleteModule"));
    throw e;
  } finally {
    toast.close();
  }
};

const clickEditModule = () => {
  isEditMode.value = !isEditMode.value;
  showDraggingTips.value = isEditMode.value;
  if (!isEditMode.value) reloadModuleList();
};

const mateStore = useMate();
const clickDeleteMate = async () => {
  try {
    await showConfirmDialog({
      title: "确定要删除伙伴吗？",
      message: "删除后将无法恢复",
      confirmButtonText: "确定",
      cancelButtonText: "取消",
    });
  } catch {
    return;
  }

  const loading = showLoadingToast(`删除中...`);
  await mateStore.deleteCurrentMate(moduleStore.module!.entity as Mate);
  loading.close();
  router.back();
};
// Lifecycle
onMounted(async () => {
  loading.value = true;
  try {
    if (route.name === "worldviewEdit") {
      entityType.value = "worldview";
    } else if (route.name === "mateEdit") {
      entityType.value = "mate";
    }
    await moduleStore.initModule(uuid.value, entityType.value!);

    currentModuleId.value = getModuleId();
    initCurrentTabIdx();
  } finally {
    loading.value = false;
  }
});
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <div class="">
      <ThreadsNav
        show-back
        :enableSwitchMate="false"
        class="border-b"
        :show-center="false"
      >
        <template #left>
          <div
            v-if="!loading"
            class="flex gap-1 text-xs md:text-sm items-center rounded-full border px-2 whitespace-nowrap py-1"
          >
            <div
              :class="[
                'rounded-full w-2 h-2',
                isSaving ? 'bg-warning' : 'bg-success',
              ]"
            />
            <span>
              {{ t(`User.settings${isSaving ? "Unsaved" : "Saved"}`) }}
            </span>
          </div>
        </template>
        <template #right>
          <div v-if="!loading && entity?.id" class="flex gap-2 items-center">
            <template v-if="entityType === 'mate'">
              <button
                :class="[
                  'btn btn-xs rounded',
                  route.params.uuid && entity.status === 0 ? 'btn-outline' : '',
                ]"
                @click="
                  router.push({
                    name: 'mateHome',
                    params: { uuid: entity.uuid },
                  })
                "
              >
                {{ t(`Buttons.viewCharacterCard`) }}
              </button>
            </template>

            <button
              v-if="route.params.uuid && entity.status !== 1"
              class="btn btn-primary btn-xs rounded"
              @click="moduleStore.module!.publish()"
            >
              {{ t("Buttons.publish") }}
            </button>

            <button
              class="btn btn-xs rounded btn-outline"
              @click="moduleStore.module?.deleteEntity()"
            >
              {{ $t(`Buttons.delete`) }}
            </button>
          </div>
        </template>
      </ThreadsNav>

      <div
        class="flex justify-between items-center border-b px-4 py-2 overflow-x-auto gap-2"
        v-if="entityType === 'mate' && !loading"
      >
        <button
          class="btn btn-xs rounded"
          @click="
            router.push({
              name: 'gallery',
              query: { mateUuid: entity?.uuid },
            })
          "
        >
          <i class="iconfont icon-gallery font-normal" />
          {{ $t(`User.mate.module.gallery`) }}
        </button>
        <button
          class="btn btn-xs rounded"
          @click="
            router.push({
              name: 'mateTimeline',
              query: { mateUuid: entity?.uuid },
            })
          "
        >
          <i class="iconfont icon-timeline font-normal" />
          {{ $t(`User.mate.module.timeline`) }}
        </button>
        <button
          class="btn btn-xs rounded"
          @click="
            router.push({
              name: 'mateMemory',
              query: { mateUuid: entity?.uuid },
            })
          "
        >
          <i class="iconfont icon-memory font-normal" />
          {{ $t(`User.mate.module.memory`) }}
        </button>

        <button
          class="btn btn-xs rounded"
          @click="
            router.push({
              name: 'mateTone',
              query: { mateUuid: entity?.uuid },
            })
          "
        >
          <i class="iconfont icon-tone font-normal" />
          {{ $t(`User.mate.module.tone`) }}
        </button>
      </div>
    </div>
    <div class="w-full h-full overflow-hidden relative">
      <div class="absolute left-0 top-1 z-20">
        <button
          v-if="!showSideBar"
          class="btn btn-xs btn-square btn-primary rounded-tl-none rounded-bl-none"
          @click="showSideBar = true"
        >
          <i class="iconify mingcute--layout-leftbar-open-line" />
        </button>
      </div>
      <div
        v-if="!loading && entity?.id"
        :class="[
          'w-full h-full grid',
          showSideBar ? 'grid-cols-[auto_1fr]' : 'grid-cols-[1fr]',
        ]"
      >
        <div
          v-if="showSideBar"
          class="border-r border-r-base-content/10 items-center overflow-hidden"
        >
          <sidebar
            :model-value="currentTabIdx"
            class="w-full h-full grid grid-rows-[1fr_auto]"
          >
            <div ref="scrollContainer" class="overflow-y-auto w-24">
              <div class="flex gap-2 items-center justify-between">
                <button
                  class="btn btn-xs btn-square btn-ghost mx-2 mt-2"
                  @click="showSideBar = false"
                >
                  <i class="iconify mingcute--layout-leftbar-close-line" />
                </button>
                <van-popover v-model:show="showDraggingTips" placement="bottom">
                  <div class="text-xs p-2">
                    {{ $t(`Message.dragModuleToSort`) }}
                  </div>
                  <template #reference>
                    <button
                      :class="[
                        'btn btn-xs btn-square mx-2 mt-2.5',
                        isEditMode ? 'text-primary' : 'btn-ghost',
                      ]"
                      v-if="showSideBar"
                      @click="clickEditModule"
                    >
                      <i class="iconify mingcute--edit-3-line" />
                    </button>
                  </template>
                </van-popover>
              </div>
              <div
                class="py-2 text-base-content relative text-base font-bold text-base-content/50 pl-2"
              >
                {{ $t(`module.${entityType}.label`) }}
              </div>

              <div class="flex flex-col gap-2">
                <div
                  :class="[
                    isEditMode && 'border-2 border-primary',
                    'rounded-sm',
                  ]"
                >
                  <sidebar-item
                    v-for="(module, i) in normalModuleList"
                    @click="clickModule(module!.moduleId)"
                    :key="module?.id"
                    :class="[
                      'py-3 text-base-content relative text-xs w-full sidebar-item',
                      dragTargetIndex === i && 'border-2 border-primary',
                      dragIndex === i && 'bg-primary/50 ',
                    ]"
                    :data-index="i"
                    :draggable="isEditMode"
                    @dragstart="dragStart($event, i)"
                    @dragenter="dragEnter($event, i)"
                    @dragend="dragEnd"
                    @dragover="dragOver"
                    @touchstart="touchStart($event, i, normalModuleList.length)"
                    @touchmove="touchMove"
                    @touchend="touchEnd"
                  >
                    <template #title>
                      <div
                        class="flex items-center justify-between gap-2 w-full"
                      >
                        <span class="flex-1"> {{ module!.module.name }} </span>
                        <!-- <span class="text-xs text-base-content/50"> -->
                        <div
                          v-if="
                            isEditMode &&
                            moduleStore.module!.canDeleteModule(
                              module!.moduleId
                            )
                          "
                          class="flex gap-1"
                        >
                          <button
                            class="btn btn-outline btn-xs rounded p-0"
                            @click.stop="clickDeleteModule(module!.moduleId)"
                          >
                            <i class="iconify mingcute--delete-2-line" />
                          </button>
                        </div>
                        <!-- </span> -->
                      </div>
                    </template>
                  </sidebar-item>
                </div>

                <button
                  class="btn btn-outline w-[90%] text-xs justify-center p-0 gap-1 items-center self-center"
                  @click="showCreateModule = true"
                >
                  <i class="iconify mingcute--plus-fill text-[10px]" />
                  <span>
                    {{ $t("User.mate.module.create") }}
                  </span>
                </button>
              </div>
            </div>
            <div class="flex flex-col border-t border-t-base-content/10">
              <div
                class="flex flex-col justify-center items-center text-base-content/50"
              >
                <sidebar-item
                  :title="t(`User.mate.privacySettings`)"
                  @click="clickModule(SETTING_MODULE_ID)"
                  class="w-full py-3 text-xs lg:text-sm"
                />
              </div>
            </div>
          </sidebar>
        </div>

        <div class="w-full overflow-x-hidden overflow-y-auto">
          <IndexModulesMate
            v-if="entityType === 'mate'"
            :mate-id="entity.id"
            :current-module-id="currentModuleId || 1"
            v-model:is-saving="isSaving"
          />
          <IndexModuleWorldview
            v-if="entityType === 'worldview'"
            :worldview-id="entity.id"
            :current-module-id="currentModuleId || 1"
            v-model:is-saving="isSaving"
          />
        </div>
      </div>
      <div v-else class="w-full h-full flex justify-center items-center">
        <div class="loading loading-ring loading-lg" />
      </div>
    </div>

    <ModuleManager
      v-model:show="showCreateModule"
      @reload-module="reloadModuleList"
    />
  </div>
</template>

<style scoped>
:deep(.sidebar-item .van-badge__wrapper) {
  @apply w-full;
}
</style>
